import { useEffect, useRef, useState } from "react";
import {
  ref,
  get,
  set,
  update,
  push,
  remove,
  onValue,
  onDisconnect,
  runTransaction,
  serverTimestamp,
  query,
  orderByValue,
  equalTo,
  limitToFirst,
} from "firebase/database";
import { rtdb } from "../firebase";
import { randomCode, ROOM_CODE_LENGTH } from "./ids";
import { deviceId } from "./admin";
import { mark, msg, showMsg, tt } from "../i18n";

// Every game needs at least `minPlayers` but has no upper limit. `best` is the
// recommended range shown to players, and `small` is an optional tip for
// groups below it.
// Names, taglines and tips are shown translated: t(game.name).
export const bestText = (g, t = tt) =>
  g.best[1] ? t("Best with {min}-{max} players", { min: g.best[0], max: g.best[1] }) : t("Best with {min}+ players", { min: g.best[0] });

// ---- Server clock ---------------------------------------------------------
// All timers use the server clock so every player sees the same countdown.
let clockOffset = 0;
onValue(ref(rtdb, ".info/serverTimeOffset"), (s) => {
  clockOffset = s.val() || 0;
});
export const realNow = () => Date.now() + clockOffset;

// The New Year countdown (lib/newYear.js) pauses the room for everyone: from
// `from` to `to` (real server time) the room's clock stands still, so every
// game timer waits, then carries on where it was. `before` is how long
// earlier countdowns already paused this room.
let pause = null;
export const setRoomPause = (p) => {
  pause = p && typeof p.from === "number" && typeof p.to === "number" ? p : null;
};
export function pausedClock(now, p) {
  if (!p) return now;
  const before = p.before || 0;
  if (now < p.from) return now - before;
  if (now < p.to) return p.from - before;
  return now - before - (p.to - p.from);
}
export const serverNow = () => pausedClock(realNow(), pause);

export const roomRef = (code, path = "") => ref(rtdb, `rooms/${code}${path ? "/" + path : ""}`);

// Rooms are listed in roomIndex/{code} under the day they were made (days
// since 1970), for the cleanup at the bottom of this file.
const DAY_MS = 86_400_000;
const dayNumber = (t) => Math.floor(t / DAY_MS);
const indexRef = (code) => ref(rtdb, `roomIndex/${code}`);

// ---- Game defaults --------------------------------------------------------
export const GAMES = {
  // `tv: true` means the game can be played Jackbox style, on one big screen
  // with everyone on their phones (see "TV mode" at the bottom of this file).
  // Not a game itself: plays a chain of random games (see games/shuffle.js).
  shuffle: {
    id: "shuffle",
    name: mark("Party Shuffle"),
    emoji: "🎲",
    tagline: mark("A random game with random rules, again and again. Who's the champion of the night?"),
    color: "#8a4dff",
    tv: true,
    minPlayers: 2,
    best: [3, 8],
    defaults: {},
  },
  doodle: {
    id: "doodle",
    name: mark("Doodle Deck"),
    emoji: "🎨",
    tagline: mark("Draw a card. Dodge the twist. Make them guess."),
    color: "#ff6b9d",
    tv: true,
    minPlayers: 2,
    best: [3, 10],
    defaults: { mode: "classic", rounds: 3, drawTime: 80, twists: true },
  },
  whoami: {
    id: "whoami",
    name: mark("Who Am I?"),
    emoji: "🃏",
    tagline: mark("Everyone sees your card - except you."),
    color: "#7c5cff",
    tv: true,
    minPlayers: 2,
    best: [3, 10],
    defaults: { mode: "deck", rounds: 3, roundTime: 0, turnTime: 30, answerTime: 60, deck: "mixed", customWords: "" },
  },
  tycoon: {
    id: "tycoon",
    name: mark("Party Tycoon"),
    emoji: "🏙️",
    tagline: mark("Buy the hottest spots in town. Bankrupt your friends."),
    color: "#2fb86a",
    tv: true,
    minPlayers: 2,
    best: [2, 6],
    defaults: { minutes: 30, theme: "party", map: "classic" },
  },
  impostor: {
    id: "impostor",
    name: mark("Impostor"),
    emoji: "🕵️",
    tagline: mark("Everyone knows the word... except the impostor."),
    color: "#e0457b",
    tv: true,
    minPlayers: 3,
    best: [4, 10],
    small: mark("With 3 players the impostor is easier to spot - keep your clues sneaky!"),
    defaults: { mode: "blank", impostors: "auto", deck: "mixed", customWords: "", clueRounds: 2, rounds: 3 },
  },
  mafia: {
    id: "mafia",
    name: mark("Mafia"),
    emoji: "🔪",
    tagline: mark("Trust no one. Vote out the killers before it's too late."),
    color: "#3b2a6b",
    tv: true,
    minPlayers: 4,
    best: [6, 15],
    small: mark("Small towns are quick and brutal - every vote counts."),
    defaults: { dayTime: 120, reveal: true },
  },
  codewords: {
    id: "codewords",
    name: mark("Code Words"),
    emoji: "🕶️",
    tagline: mark("Two teams. One-word clues. Don't touch the assassin."),
    color: "#e2533b",
    tv: true,
    minPlayers: 4,
    best: [4, 12],
    defaults: {},
  },
  telephone: {
    id: "telephone",
    name: mark("Doodle Telephone"),
    emoji: "📞",
    tagline: mark("Write it. Draw it. Guess it. Watch it go hilariously wrong."),
    color: "#14a3a3",
    tv: true,
    minPlayers: 2,
    best: [4, 10],
    small: mark("Fewer players means shorter chains - still plenty of chaos."),
    defaults: { mode: "classic", speed: "normal" },
  },
  punchline: {
    id: "punchline",
    name: mark("Punchline Duel"),
    emoji: "🥊",
    tagline: mark("Two answers enter. The funniest one wins."),
    color: "#e8772e",
    tv: true,
    minPlayers: 3,
    best: [3, 8],
    defaults: { rounds: 3, writeTime: 90 },
  },
  bluffalo: {
    id: "bluffalo",
    name: mark("Bluffalo"),
    emoji: "🦬",
    tagline: mark("Weird true facts. Fake answers. Can you spot the real one?"),
    color: "#9a6b3f",
    tv: true,
    minPlayers: 2,
    best: [3, 8],
    defaults: { questions: 7, writeTime: 60 },
  },
  split: {
    id: "split",
    name: mark("Split Decision"),
    emoji: "⚖️",
    tagline: mark("Finish the scenario. Split the room right down the middle."),
    color: "#2d7ff9",
    tv: true,
    minPlayers: 3,
    best: [4, 10],
    small: mark("With 3 players each scenario only gets two votes, so it's a perfect split or nothing."),
    defaults: { rounds: 2, writeTime: 75 },
  },
  lastcard: {
    id: "lastcard",
    name: mark("Last Card"),
    emoji: "🎴",
    tagline: mark("Match the colour, dodge the +4, and don't forget to shout Last card!"),
    color: "#ff5a36",
    tv: true,
    minPlayers: 2,
    best: [3, 8],
    defaults: { rounds: 3, stacking: false, turnTime: 30 },
  },
  fakeartist: {
    id: "fakeartist",
    name: mark("Fake Artist"),
    emoji: "🖌️",
    tagline: mark("One drawing, one line at a time. One artist is faking it."),
    color: "#c27c0e",
    tv: true,
    minPlayers: 3,
    best: [4, 10],
    small: mark("With 3 players there's nowhere to hide - draw carefully!"),
    defaults: { deck: "mixed", customWords: "", rounds: 3, laps: 2, turnTime: 30 },
  },
  vibedial: {
    id: "vibedial",
    name: mark("Vibe Dial"),
    emoji: "🎛️",
    tagline: mark("One clue, one hidden target. How close can you turn the dial?"),
    color: "#0b8fc4",
    tv: true,
    minPlayers: 2,
    best: [3, 10],
    defaults: { rounds: 1 },
  },
  rummy: {
    id: "rummy",
    name: mark("Rummy"),
    emoji: "♦️",
    tagline: mark("Draw, lay down sets and runs, and empty your hand first."),
    color: "#c2410c",
    tv: true,
    minPlayers: 2,
    best: [2, 6],
    defaults: { rounds: 3, turnTime: 60 },
  },
  // Casino tables: only for players the site owner approves (see lib/admin.js
  // and the /casino page), and never listed with the other games.
  blackjack: {
    id: "blackjack",
    name: mark("Blackjack"),
    emoji: "♠️",
    tagline: mark("Beat the dealer to 21 with play chips."),
    color: "#1f7a4d",
    minPlayers: 1,
    best: [1, 6],
    casino: true,
    defaults: { rounds: 10, turnTime: 20, chips: 1000 },
  },
  roulette: {
    id: "roulette",
    name: mark("Roulette"),
    emoji: "🎰",
    tagline: mark("Red or black? Bet play chips and spin the wheel."),
    color: "#b3243b",
    minPlayers: 1,
    best: [1, 8],
    casino: true,
    defaults: { rounds: 10, chips: 1000 },
  },
  poker: {
    id: "poker",
    name: mark("Texas Hold'em"),
    emoji: "♣️",
    tagline: mark("Bluff, bet and go all in with play chips."),
    color: "#2b3a67",
    minPlayers: 2,
    best: [3, 8],
    casino: true,
    defaults: { rounds: 20, chips: 1000, smallBlind: 10, turnTime: 30 },
  },
};
// The games everyone sees (the casino tables are hidden).
export const PUBLIC_GAMES = Object.fromEntries(Object.entries(GAMES).filter(([, g]) => !g.casino));

// ---- Room lifecycle -------------------------------------------------------
// `lang` is the language of the room's words and prompts (the host can change it).
// `tv`: made from the big screen (TV mode is on from the start).
// `public`: open to Quick match from the start (rooms are private otherwise).
export async function createRoom(game, uid, lang = "en", { tv = false, public: open = false } = {}) {
  for (let attempt = 0; attempt < 8; attempt++) {
    const code = randomCode(ROOM_CODE_LENGTH);
    const createdAt = serverNow();
    const res = await runTransaction(roomRef(code, "meta"), (cur) =>
      cur
        ? undefined
        : {
            game,
            hostId: uid,
            status: "lobby",
            createdAt,
            lang,
            settings: { ...GAMES[game].defaults },
            ...(tv ? { tv: true } : {}),
            ...(open ? { public: true } : {}),
          }
    );
    if (res.committed) {
      // List it for the cleanup (see sweepStaleRooms). Best effort: the room
      // works without it.
      set(indexRef(code), dayNumber(createdAt)).catch(() => {});
      return code;
    }
  }
  throw new Error(tt("Couldn't create a room, please try again."));
}

export async function fetchRoomMeta(code) {
  const snap = await get(roomRef(code, "meta"));
  return snap.exists() ? snap.val() : null;
}

export async function leaveRoom(code, uid, meta, players) {
  markLeft(code, uid);
  const pRef = roomRef(code, `players/${uid}`);
  await onDisconnect(pRef).cancel();
  if (meta?.hostId === uid) {
    // (A big screen never takes over.)
    const next = Object.entries(players || {})
      .filter(([id, p]) => id !== uid && p.online && !p.screen)
      .sort((a, b) => a[1].joinedAt - b[1].joinedAt)[0];
    if (!next) {
      // Last one out turns off the lights.
      await remove(roomRef(code));
      remove(indexRef(code)).catch(() => {});
      return;
    }
    await set(roomRef(code, "meta/hostId"), next[0]);
  }
  await remove(pRef);
}

export const kickPlayer = (code, uid) =>
  update(roomRef(code), { [`players/${uid}`]: null, [`meta/kicked/${uid}`]: true });

// The host passes the crown to another player (only if they're still host).
export async function handOverHost(code, from, to, name) {
  const res = await runTransaction(roomRef(code, "meta/hostId"), (cur) => (cur === from ? to : undefined));
  if (res.committed) await sendChat(code, systemMsg(msg("👑 {name} is the host now.", { name }), { tone: "good" }));
  return res.committed;
}

export const sendChat = (code, msg) => push(roomRef(code, "chat"), { ...msg, t: serverTimestamp() });

// A chat message from the game. `text` is plain text or a msg(): then every
// player reads it in their own language (text keeps the English version).
export const systemMsg = (text, extra = {}) =>
  text && typeof text === "object"
    ? { type: "system", text: showMsg("en", text), i18n: text, ...extra }
    : { type: "system", text, ...extra };

// "Ava guessed it! +300" in the chat, shown in each player's language.
export const correctMsg = (uid, name, m) => ({ type: "correct", uid, name, text: showMsg("en", m), i18n: m });

// A move sent with pushEvent waits for the host's browser, which deletes the
// event once it has taken the move. Until then the button that sent it is
// marked "sending" (button.sending in App.css), so a press shows it counted
// even while the answer is on its way. At most SENDING_MAX_MS.
const SENDING_MAX_MS = 8000;
let lastPress = null;
if (typeof document !== "undefined") {
  document.addEventListener("click", (e) => (lastPress = { el: e.target.closest?.("button"), at: Date.now() }), true);
}
function markSending(evRef) {
  const el = lastPress && Date.now() - lastPress.at < 500 ? lastPress.el : null;
  if (!el) return;
  el.classList.add("sending");
  let finished = false;
  let off = null;
  const done = () => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    el.classList.remove("sending");
    off?.();
  };
  const timer = setTimeout(done, SENDING_MAX_MS);
  off = onValue(evRef, (s) => !s.exists() && done(), done);
  // The host's own moves can be taken before we even start listening.
  if (finished) off();
}

export function pushEvent(code, ev) {
  const evRef = push(roomRef(code, "events"), { ...ev, t: serverTimestamp() });
  markSending(evRef);
  return evRef;
}

// Update several paths under the room atomically, optionally appending chat
// messages in the same write.
export function roomUpdate(code, patch, chatMessages = []) {
  const full = { ...patch };
  for (const m of chatMessages) {
    const key = push(roomRef(code, "chat")).key;
    full[`chat/${key}`] = { ...m, t: serverTimestamp() };
  }
  return update(roomRef(code), full);
}

// Everything a game leaves behind in the room, cleared between games.
export const RESET_PATHS = {
  state: null,
  strokes: null,
  cards: null,
  hints: null,
  word: null,
  events: null,
  secret: null,
  answer: null,
  mafiachat: null,
  nightacts: null,
  key: null,
  chains: null,
  // A vote to end the game early (see components/EndVote.js).
  "meta/endVote": null,
};

export function backToLobby(code) {
  return update(roomRef(code), { "meta/status": "lobby", ...RESET_PATHS });
}

// Reads a path that only some players may read (e.g. your own secret role).
// Re-subscribes when `deps` change, since permission can change with the game phase.
export function usePrivateValue(code, path, enabled = true, deps = []) {
  const [value, setValue] = useState(null);
  const depKey = JSON.stringify(deps);
  useEffect(() => {
    setValue(null);
    if (!code || !path || !enabled) return;
    return onValue(roomRef(code, path), (s) => setValue(s.val()), () => setValue(null));
  }, [code, path, enabled, depKey]);
  return value;
}

// ---- Hooks ----------------------------------------------------------------

// Subscribes to the public parts of a room. (The room root itself isn't
// readable - secret paths like cards/word have their own rules.)
export function useRoom(code) {
  const [meta, setMeta] = useState(undefined);
  const [players, setPlayers] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!code) return;
    const offMeta = onValue(
      roomRef(code, "meta"),
      (s) => setMeta(s.exists() ? s.val() : null),
      (e) => setError(e)
    );
    const offPlayers = onValue(roomRef(code, "players"), (s) => setPlayers(s.val() || {}), () => {});
    return () => {
      offMeta();
      offPlayers();
    };
  }, [code]);

  return { meta, players, error };
}

// State is readable only by members, so it's subscribed once we've joined.
export function useRoomState(code, joined) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!code || !joined) return;
    return onValue(roomRef(code, "state"), (s) => setState(s.val()), () => {});
  }, [code, joined]);
  return state;
}

// This page load's own id. It's written to players/{uid}/session when the page
// joins, so when the same account opens the room on a second device we can tell
// whose turn to be online it is (see useSessionTaken below).
export const SESSION = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
// The rooms this page has played in (code/uid). A page that has been taken over
// never takes its place back by itself, even if its joining restarts (see
// usePresence): only "Use this device instead" does, through releaseRoom.
const joinedHere = new Set();
const TAKEN = "partycade:session-taken";
export const releaseRoom = (code, uid) => joinedHere.delete(`${code}/${uid}`);

// Keeps our player entry alive: marks us online whenever the connection is
// (re)established and offline when it drops.
// One account is one player, so it's online on one device at a time: the
// newest device to open the room takes over, and an older one stops (it never
// writes to the player entry again, so two devices can't fight over it).
// Rooms this page left on purpose (the door button). While the page is still
// on screen, nothing may add the player back: a late profile update or a
// reconnect would otherwise recreate the entry and put them back in the game.
const leftOnPurpose = new Set();
export const markLeft = (code, uid) => leftOnPurpose.add(`${code}/${uid}`);
export const isLeft = (code, uid) => leftOnPurpose.has(`${code}/${uid}`);
export const unmarkLeft = (code, uid) => leftOnPurpose.delete(`${code}/${uid}`);

export function usePresence(code, uid, info, enabled) {
  const [joined, setJoined] = useState(false);
  const infoRef = useRef(info);
  infoRef.current = info;

  useEffect(() => {
    if (!enabled || !code || !uid) return;
    const pRef = roomRef(code, `players/${uid}`);
    const key = `${code}/${uid}`;
    let cancelled = false;
    let stopHeal = null;

    const off = onValue(ref(rtdb, ".info/connected"), async (s) => {
      if (!s.val() || cancelled || leftOnPurpose.has(key)) return;
      try {
        const existing = await get(pRef);
        const other = existing.exists() && existing.val().session && existing.val().session !== SESSION;
        // Another device of this account took over while this page was away, or
        // since this page last played here: leave it be and tell the page.
        if (joinedHere.has(key) && other) {
          window.dispatchEvent(new CustomEvent(TAKEN, { detail: key }));
          return;
        }
        const base = existing.exists() ? existing.val() : { joinedAt: serverNow() };
        const away = typeof document !== "undefined" && document.visibilityState === "hidden" ? serverNow() : null;
        // `device` lets bans cover this browser too (see lib/admin.js).
        await set(pRef, { ...base, ...infoRef.current, online: true, lastSeen: serverNow(), away, device: deviceId(), session: SESSION });
        joinedHere.add(key);
        // Registered after the entry exists: the rules reject a nameless player node.
        await onDisconnect(pRef).update({ online: false, lastSeen: serverTimestamp() });
        // If something marks us offline while we're connected and still the
        // device that's playing (the "offline" a taken-over device leaves behind
        // when its connection finally drops), say we're online again.
        stopHeal?.();
        stopHeal = onValue(pRef, (snap) => {
          const v = snap.val();
          if (!cancelled && v && v.online === false && v.session === SESSION) update(pRef, { online: true, lastSeen: serverTimestamp() }).catch(() => {});
        });
        if (!cancelled) setJoined(true);
      } catch (e) {
        console.error("Failed to join room", e);
      }
    });

    return () => {
      cancelled = true;
      off();
      stopHeal?.();
      // Navigating away marks us offline, the Leave button removes us fully, and
      // an older device that was taken over leaves the entry alone (the newer
      // device is the one that's online now).
      get(pRef)
        .then((snap) => snap.val()?.session === SESSION && update(pRef, { online: false, lastSeen: serverTimestamp() }))
        .catch(() => {});
      onDisconnect(pRef).cancel().catch(() => {});
      setJoined(false);
    };
  }, [code, uid, enabled]);

  // While anyone is in the room, it's marked active (see "Tidying up" below).
  useEffect(() => {
    if (!joined) return;
    const beat = () => set(roomRef(code, "meta/active"), serverTimestamp()).catch(() => {});
    beat();
    const id = setInterval(beat, ACTIVE_BEAT_MS);
    return () => clearInterval(id);
  }, [joined, code]);

  // Push profile changes (name/avatar/level) into the room while joined.
  const infoKey = JSON.stringify(info);
  useEffect(() => {
    if (!joined || leftOnPurpose.has(`${code}/${uid}`)) return;
    update(roomRef(code, `players/${uid}`), JSON.parse(infoKey)).catch(() => {});
  }, [joined, code, uid, infoKey]);

  return joined;
}

// Calls `onTaken` when another device of this account joined the room after
// us: its page load id is now in players/{uid}/session instead of ours.
export function useSessionTaken(code, uid, joined, onTaken) {
  const cb = useRef(onTaken);
  cb.current = onTaken;
  // (Joining again after being taken over doesn't happen by itself, so the
  // watcher below can't see it; the join code tells the page directly.)
  useEffect(() => {
    if (!code || !uid) return;
    const key = `${code}/${uid}`;
    const on = (e) => e.detail === key && cb.current();
    window.addEventListener(TAKEN, on);
    return () => window.removeEventListener(TAKEN, on);
  }, [code, uid]);
  useEffect(() => {
    if (!joined || !code || !uid) return;
    return onValue(
      roomRef(code, `players/${uid}/session`),
      (s) => {
        const other = s.val();
        if (other && other !== SESSION) cb.current();
      },
      () => {}
    );
  }, [code, uid, joined]);
}

// If the host disappears, the longest-standing active player takes over.
// The host's browser runs the game (timers, turns, scoring) and checks in
// every few seconds (players/{host}/lastSeen) for as long as it runs. On a
// computer that includes a hidden tab or a covered window, so looking at
// another tab or app doesn't cost the host the crown. (Deciding by "page
// hidden" did: hosts lost it after 8 seconds in Discord, or because a second
// tab of the room was in the background.) The host counts as gone when:
// - they haven't checked in for a while: their page stopped, or a tab has
//   been hidden for many minutes (browsers then slow it right down),
// - their page is hidden and has missed a few check-ins: a phone put away,
//   which stops the page (spotted sooner this way),
// - or they've been disconnected for a few seconds: they closed the page.
//   A short drop in the connection doesn't count.
// A player whose page is hidden marks themselves away (players/{uid}/away),
// and away players aren't picked to take over.
const HOST_BEAT_MS = 3000;
const HOST_STALE_MS = 15000;
const AWAY_STALE_MS = 10000;
const OFFLINE_GRACE_MS = 5000;
const TAKEOVER_WAIT_MS = 3000;

export function hostStalled(host, now = serverNow()) {
  if (!host) return true;
  const quiet = now - (host.lastSeen || 0);
  return quiet > HOST_STALE_MS || (!host.online && quiet > OFFLINE_GRACE_MS) || (!!host.away && quiet > AWAY_STALE_MS);
}

// Who should take over: the longest-standing player who's online and looking at the page.
export function nextHost(players, hostId, now = serverNow()) {
  const next = Object.entries(players || {})
    .filter(([id, p]) => id !== hostId && p.online && !p.away && !p.screen)
    .sort((a, b) => a[1].joinedAt - b[1].joinedAt)[0];
  return next ? next[0] : null;
}

// TV mode: the VIP is the first player who isn't a screen. They start the
// game and press the host's buttons from their phone (see useHostElection).
export function vipId(players) {
  const first = Object.entries(players || {})
    .filter(([, p]) => p.online && !p.screen)
    .sort((a, b) => a[1].joinedAt - b[1].joinedAt)[0];
  return first ? first[0] : null;
}

export function useHostElection(code, meta, players, uid, joined) {
  const latest = useRef({ meta, players });
  latest.current = { meta, players };
  const isHost = !!meta && meta.hostId === uid;

  // The host checks in.
  useEffect(() => {
    if (!joined || !isHost) return;
    const beat = () => update(roomRef(code, `players/${uid}`), { lastSeen: serverNow() }).catch(() => {});
    beat();
    const id = setInterval(beat, HOST_BEAT_MS);
    return () => clearInterval(id);
  }, [joined, isHost, code, uid]);

  // Everyone marks themselves away while their page is hidden.
  useEffect(() => {
    if (!joined || !uid) return;
    const pRef = roomRef(code, `players/${uid}`);
    const sync = () => update(pRef, { away: document.visibilityState === "hidden" ? serverNow() : null }).catch(() => {});
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, [joined, uid, code]);

  // TV mode: the big screen doesn't run the game. A room made on a TV starts
  // with the screen as host, and the VIP takes the crown from it as soon as
  // they're there.
  useEffect(() => {
    if (!joined || !uid) return;
    const check = () => {
      const { meta: m, players: ps } = latest.current;
      if (!m?.tv || m.hostId === uid || !ps?.[m.hostId]?.screen || vipId(ps) !== uid) return;
      const screenHost = m.hostId;
      runTransaction(roomRef(code, "meta/hostId"), (cur) => (cur === screenHost ? uid : undefined)).catch(() => {});
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [joined, uid, code]);

  // The next player in line takes over once the host has been gone for a moment.
  useEffect(() => {
    if (!joined || !uid) return;
    let since = null;
    const check = () => {
      const { meta: m, players: ps } = latest.current;
      const now = serverNow();
      if (!m || m.hostId === uid || !hostStalled(ps?.[m.hostId], now) || nextHost(ps, m.hostId, now) !== uid) {
        since = null;
        return;
      }
      if (!since || since.host !== m.hostId) since = { host: m.hostId, at: now };
      if (now - since.at < TAKEOVER_WAIT_MS) return;
      since = null;
      const oldHost = m.hostId;
      runTransaction(roomRef(code, "meta/hostId"), (cur) => (cur === oldHost ? uid : undefined)).catch(() => {});
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [joined, uid, code]);
}

// Runs `tick` every `ms` while we're the host. `tick` always sees the latest
// closure because it is read through a ref.
export function useHostLoop(isHost, tick, ms = 400) {
  const tickRef = useRef(tick);
  tickRef.current = tick;
  useEffect(() => {
    if (!isHost) return;
    const id = setInterval(() => {
      try {
        tickRef.current();
      } catch (e) {
        console.error(e);
      }
    }, ms);
    return () => clearInterval(id);
  }, [isHost, ms]);
}

// A ticking server-synced clock for countdown displays.
export function useNow(ms = 250) {
  const [now, setNow] = useState(serverNow());
  useEffect(() => {
    const id = setInterval(() => setNow(serverNow()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

// ---- Tidying up -------------------------------------------------------------
// Rooms are temporary. While anyone is in a room, their browser marks it
// active every few minutes (meta/active). The database rules let anyone
// delete a room nobody has marked for IDLE_MS (or, for rooms from before
// meta/active, one made over 6 hours ago), and any room over a day old.
// The sweeps that find them:
// - sweepOldRooms: each browser remembers the rooms it has been in, and
//   closes the idle ones the next time the site opens.
// - sweepStaleRooms: any visitor can list the rooms made 2 to 8 days ago
//   (roomIndex, by day) and deletes the ones nobody is in. The rules only let
//   you list those old days, so the codes of live rooms stay private.
// - The site owner's browser checks every room (closeIdleRooms in admin.js).
export const IDLE_MS = 30 * 60_000;
const ACTIVE_BEAT_MS = 5 * 60_000;
export const roomIdle = (meta, now = serverNow()) =>
  typeof meta?.active === "number" ? now - meta.active > IDLE_MS : now - (meta?.createdAt || 0) > 6 * 3_600_000;
const VISITED_KEY = "partycade.rooms";
const SWEPT_KEY = "partycade.swept";

export async function sweepStaleRooms() {
  try {
    if (Date.now() - Number(localStorage.getItem(SWEPT_KEY) || 0) < 6 * 3_600_000) return;
    localStorage.setItem(SWEPT_KEY, String(Date.now()));
  } catch {}
  const today = dayNumber(serverNow());
  for (let back = 2; back <= 8; back++) {
    const old = await get(query(ref(rtdb, "roomIndex"), orderByValue(), equalTo(today - back), limitToFirst(10))).catch(() => null);
    const codes = [];
    old?.forEach((s) => {
      codes.push(s.key);
    });
    for (const code of codes) {
      // Leave it alone while someone's still playing in it (for up to a week).
      const players = (await get(roomRef(code, "players")).catch(() => null))?.val() || {};
      if (back < 7 && Object.values(players).some((p) => p.online)) continue;
      await remove(roomRef(code)).catch(() => {});
      await remove(indexRef(code)).catch(() => {});
    }
  }
}

function visitedRooms() {
  try {
    const list = JSON.parse(localStorage.getItem(VISITED_KEY));
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
function saveVisited(list) {
  try {
    localStorage.setItem(VISITED_KEY, JSON.stringify(list.slice(-50)));
  } catch {}
}

export function rememberVisit(code) {
  const list = visitedRooms();
  if (!list.some((r) => r.code === code)) saveVisited([...list, { code, at: Date.now() }]);
}

export async function sweepOldRooms() {
  const keep = [];
  for (const r of visitedRooms()) {
    try {
      const [meta, players] = await Promise.all([get(roomRef(r.code, "meta")), get(roomRef(r.code, "players"))]);
      if (!meta.exists()) continue;
      const inUse = Object.values(players.val() || {}).some((p) => p.online);
      if (inUse || !roomIdle(meta.val())) keep.push(r);
      else {
        await remove(roomRef(r.code));
        remove(indexRef(r.code)).catch(() => {});
      }
    } catch {
      // Not allowed or offline: try again another day, for a week at most.
      if (Date.now() - r.at < 7 * DAY_MS) keep.push(r);
    }
  }
  saveVisited(keep);
}

// A player who drops out mid-game keeps their seat this long, so they can come
// back (see lib/rejoin.js).
export const REJOIN_MS = 5 * 60_000;
export const recentlyLeft = (p, now = serverNow()) => !!p && !p.online && now - (p.lastSeen || 0) < REJOIN_MS;

// Everyone still in the game: online, or dropped out less than REJOIN_MS ago.
// Games only end early for lack of players once saved seats run out.
export const seatedPlayers = (players) =>
  Object.keys(players || {}).filter((id) => !players[id].screen && (players[id].online || recentlyLeft(players[id])));

// Players only: the big screen in TV mode is in the room but isn't playing.
export const onlinePlayers = (players) =>
  Object.entries(players || {})
    .filter(([, p]) => p.online && !p.screen)
    .map(([id, p]) => ({ id, ...p }))
    .sort((a, b) => a.joinedAt - b.joinedAt);

// ---- TV mode ----------------------------------------------------------------
// Jackbox style: one device shows the game on a big screen and everyone plays
// from their own phone. It's optional and only for games with `tv: true`.
// meta.tv switches it on. A device that's the screen marks its player entry
// with `screen: true` (see Room.js): it sees the game, but isn't a player, so
// it's left out of onlinePlayers/seatedPlayers.
export const tvSupported = (game) => !!GAMES[game]?.tv;
export const isTv = (meta) => !!meta?.tv && tvSupported(meta.game);
export const screenIds = (players) =>
  Object.entries(players || {})
    .filter(([, p]) => p.screen && p.online)
    .map(([id]) => id);

export const setTvMode = (code, on) => update(roomRef(code, "meta"), { tv: on ? true : null });
